import type { PcapPeriodResult, PcapPeriodSet } from './types';

export default function buildPcapTableRows(
  lp: PcapPeriodSet,
  fund: PcapPeriodSet,
) {
  function row(
    label: string,
    getValue: (p: PcapPeriodResult) => number,
    opts?: {
      isTotal?: boolean;
      isSectionHeader?: boolean;
      subtitle?: string;
      isSubtleBackground?: boolean;
    },
  ) {
    if (opts?.isSectionHeader) {
      return {
        label,
        isSectionHeader: true,
        lpQtd: 0,
        lpYtd: 0,
        lpSi: 0,
        fundQtd: 0,
        fundYtd: 0,
        fundSi: 0,
      };
    }
    return {
      label,
      subtitle: opts?.subtitle,
      isTotal: opts?.isTotal,
      isSubtleBackground: opts?.isSubtleBackground,
      lpQtd: getValue(lp.qtd),
      lpYtd: getValue(lp.ytd),
      lpSi: getValue(lp.sinceInception),
      fundQtd: getValue(fund.qtd),
      fundYtd: getValue(fund.ytd),
      fundSi: getValue(fund.sinceInception),
    };
  }

  const capitalMovementRows = [
    row('Capital movement', () => 0, { isSectionHeader: true }),
    row('Total commitment', (p) => p.capitalMovement.totalCommitment),
    row(
      'Unfunded commitment at start of period',
      (p) => p.capitalMovement.unfundedCommitmentAtStart,
      { isSubtleBackground: true },
    ),
    row(
      'Contributions',
      (p) => p.capitalMovement.contributionsInsideCommitment,
      {
        subtitle: 'Inside commitment',
      },
    ),
    row(
      'Recallable distributions',
      (p) => p.capitalMovement.recallableDistributions,
    ),
    row(
      'Unfunded commitment at end of period',
      (p) => p.capitalMovement.unfundedCommitmentAtEnd,
      { isSubtleBackground: true },
    ),
    row(
      'Contributions',
      (p) => p.capitalMovement.contributionsOutsideCommitment,
      {
        subtitle: 'Outside commitment',
      },
    ),
    row(
      'Non-recallable distributions',
      (p) => p.capitalMovement.nonRecallableDistributions,
    ),
    row('Net capital movement', (p) => p.capitalMovement.netCapitalMovement, {
      isTotal: true,
      subtitle: 'Contributions minus distributions',
    }),
  ];

  const allExpenseLabels = new Set<string>([
    ...lp.qtd.expenses.byCategory.map((c) => c.label),
    ...lp.ytd.expenses.byCategory.map((c) => c.label),
    ...lp.sinceInception.expenses.byCategory.map((c) => c.label),
    ...fund.qtd.expenses.byCategory.map((c) => c.label),
    ...fund.ytd.expenses.byCategory.map((c) => c.label),
    ...fund.sinceInception.expenses.byCategory.map((c) => c.label),
  ]);

  const expenseRows = [
    row('Expenses', () => 0, { isSectionHeader: true }),
    row('Management fees', (p) => p.expenses.managementFees, {
      subtitle: 'Before offset',
    }),
    ...[...allExpenseLabels].map((label) => ({
      label: 'Partnership expenses',
      subtitle: label,
      lpQtd:
        lp.qtd.expenses.byCategory.find((c) => c.label === label)?.value ?? 0,
      lpYtd:
        lp.ytd.expenses.byCategory.find((c) => c.label === label)?.value ?? 0,
      lpSi:
        lp.sinceInception.expenses.byCategory.find((c) => c.label === label)
          ?.value ?? 0,
      fundQtd:
        fund.qtd.expenses.byCategory.find((c) => c.label === label)?.value ?? 0,
      fundYtd:
        fund.ytd.expenses.byCategory.find((c) => c.label === label)?.value ?? 0,
      fundSi:
        fund.sinceInception.expenses.byCategory.find((c) => c.label === label)
          ?.value ?? 0,
    })),
    row('Total expenses', (p) => p.expenses.totalExpenses, { isTotal: true }),
  ];

  const allIncomeLabels = new Set<string>([
    ...lp.qtd.income.byCategory.map((c) => c.label),
    ...lp.ytd.income.byCategory.map((c) => c.label),
    ...lp.sinceInception.income.byCategory.map((c) => c.label),
    ...fund.qtd.income.byCategory.map((c) => c.label),
    ...fund.ytd.income.byCategory.map((c) => c.label),
    ...fund.sinceInception.income.byCategory.map((c) => c.label),
  ]);

  const incomeRows = [
    row('Income', () => 0, { isSectionHeader: true }),
    ...[...allIncomeLabels].map((label) => ({
      label,
      lpQtd:
        lp.qtd.income.byCategory.find((c) => c.label === label)?.value ?? 0,
      lpYtd:
        lp.ytd.income.byCategory.find((c) => c.label === label)?.value ?? 0,
      lpSi:
        lp.sinceInception.income.byCategory.find((c) => c.label === label)
          ?.value ?? 0,
      fundQtd:
        fund.qtd.income.byCategory.find((c) => c.label === label)?.value ?? 0,
      fundYtd:
        fund.ytd.income.byCategory.find((c) => c.label === label)?.value ?? 0,
      fundSi:
        fund.sinceInception.income.byCategory.find((c) => c.label === label)
          ?.value ?? 0,
    })),
    row('Total income', (p) => p.income.totalIncome, { isTotal: true }),
  ];

  const navRows = [
    row('NAV reconciliation', () => 0, { isSectionHeader: true }),
    row('Beginning NAV', (p) => p.navReconciliation.beginningNav, {
      isSubtleBackground: true,
    }),
    row('Net capital movement', (p) => p.navReconciliation.netCapitalMovement, {
      subtitle: 'Contributions minus distributions',
    }),
    row('Total expenses', (p) => p.navReconciliation.totalExpenses),
    row('Total income', (p) => p.navReconciliation.totalIncome),
    row(
      'Realised gain/loss in period',
      (p) => p.navReconciliation.realisedGainLoss,
    ),
    row(
      'Change in unrealised gain/loss in period',
      (p) => p.navReconciliation.unrealisedGainLossChange,
    ),
    row('Ending NAV', (p) => p.navReconciliation.endingNavNetOfCarry, {
      isTotal: true,
      subtitle: 'Net of carried interest',
    }),
  ];

  const carryRows = [
    row('Carried interest', () => 0, { isSectionHeader: true }),
    row(
      'Accrued carried interest at start of period',
      (p) => p.carriedInterest.accruedCarryAtStart,
    ),
    row(
      'Carried interest distributed in the period',
      (p) => p.carriedInterest.carryDistributedInPeriod,
    ),
    row(
      'Accrued carried interest earned/(lost) in the period',
      (p) => p.carriedInterest.carryEarnedLostInPeriod,
    ),
    row(
      'Accrued carried interest at end of period',
      (p) => p.carriedInterest.accruedCarryAtEnd,
    ),
    row('Ending NAV', (p) => p.carriedInterest.endingNavGrossOfCarry, {
      isTotal: true,
      subtitle: 'Gross of carried interest',
    }),
  ];

  return [
    ...capitalMovementRows,
    ...expenseRows,
    ...incomeRows,
    ...navRows,
    ...carryRows,
  ];
}
